// Workstream B #34 — the per-engine CASE CONTEXT the identity comparators are
// decided under, and the component primitives that apply it to ONE identifier
// component. Split out of `relationIdentity.ts`, which keeps the overview of
// WHY each engine compares the way it does and stays the import surface every
// caller outside this folder uses.
//
// The low-level component helpers (`leafComponent`, `catalogComponentEq`, …)
// are exported for `relationIdentity.ts` and `catalogLeafCase.ts` only — the
// facade is what the rest of the app imports.

import type { SqlEngine } from '../sql-grammar/model';

import type { IdentifierIndex, QualifiedName } from './types';

export type RelationIdentityContext = {
  readonly engine: SqlEngine;
  /** MSSQL only. True when the DATABASE's collation tells `dbo.V` from
   *  `dbo.v`, i.e. they are two objects. Defaults to false (the common `_CI_`
   *  server default). Derive it with {@link mssqlCatalogCaseSensitiveFor}
   *  rather than by hand: the value is the per-database collation the
   *  lifecycle probed, and the index handle is the only place that reports the
   *  rule it actually keyed its objects with.
   *
   *  This describes OBJECT and SCHEMA names only. DATABASE names follow a
   *  DIFFERENT collation — the instance's — carried separately in
   *  {@link RelationIdentityContext.mssqlDatabaseNamesCaseSensitive}. */
  readonly mssqlCatalogCaseSensitive?: boolean;
  /** MSSQL only. True when the SERVER (instance) collation tells the database
   *  `Sales` from the database `sales`, i.e. they are two databases. Read it
   *  from `mssqlCollationProbe.ts`'s `mssqlDatabaseNamesCaseSensitive(
   *  connectionId)` — one probe per CONNECTION, because one instance collation
   *  answers it for every database the connection spans. Defaults to false
   *  (the common `_CI_` instance default), which is also what an unprobed
   *  connection gets: over-inclusion is the safe direction for discovery. */
  readonly mssqlDatabaseNamesCaseSensitive?: boolean;
  /** MySQL only. `@@lower_case_table_names`: 0 = case-sensitive stored case,
   *  1/2 = case-insensitive. Undefined → treated case-insensitive. */
  readonly mysqlLowerCaseTableNames?: number;
};

export function relationIdentityContextFor(
  engine: SqlEngine,
  opts?: Pick<
    RelationIdentityContext,
    | 'mssqlCatalogCaseSensitive'
    | 'mssqlDatabaseNamesCaseSensitive'
    | 'mysqlLowerCaseTableNames'
  >,
): RelationIdentityContext {
  return { engine, ...opts };
}

/** One identifier component in BOTH spellings — as the source/catalog wrote it
 *  (`spelled`) and as the engine folds it (`folded`). Case-sensitive engines
 *  and collations are decided on the SPELLED half; see
 *  {@link catalogComponentEq}. */
export type DatabaseComponent = { readonly spelled: string; readonly folded: string };

/** The database a rename / search resolves against — the ROLE name for the
 *  component `relationIdentity.ts`'s `effectiveDatabaseFor` builds, so the
 *  comparators can say which of the two databases in a comparison is the
 *  target's.
 *
 *  It used to admit a bare string as well, for callers that had only the FOLDED
 *  form. None is left: `rename/tabRewritePlanner.ts` (the caller that shape was
 *  kept for), `rename/renameReferenceCollection.ts` and
 *  `find-references/findReferencesService.ts` all build theirs with
 *  {@link effectiveDatabaseFor}, which carries both halves. The string arm and
 *  its "spelling unknown" conversion were deleted rather than left with a
 *  comment naming a caller that no longer exists. */
export type EffectiveDatabase = DatabaseComponent;

/** The identity components MySQL compares case-INSENSITIVELY in every
 *  `@@lower_case_table_names` mode, because the setting does not reach them:
 *  it governs the filesystem (a schema is a directory, a table / view a file),
 *  and neither a stored routine's own name nor a COLUMN name is a filesystem
 *  entry. The server folds routine names itself, and column names live inside a
 *  table definition and are compared case-insensitively on every server.
 *
 *  `mysqlCaseProbe.ts`'s `mysqlObjectKeyCase` — the rule the identifier index
 *  keys its records with — already says exactly this (`name: false` for every
 *  kind that is not a view or a trigger). Leaving a column out of this set made
 *  the comparator and the index disagree about columns: renaming `users.id`
 *  left an open tab's valid `u.ID` unrewritten
 *  (`rename/tabRewritePlanner.column.ts`). */
const MYSQL_CASE_INSENSITIVE_PARTS: ReadonlySet<IdentityComponent> = new Set([
  'routine-leaf',
  'column-leaf',
]);

/** True when MySQL's `@@lower_case_table_names` makes THIS component
 *  case-significant on this connection.
 *
 *  `0` (the Linux default, and what the docker test server reports) stores a
 *  schema as a DIRECTORY and a table / view as a FILE, so `AppDb` and `appdb`
 *  are two schemas and `Users` and `users` two tables. A stored ROUTINE's own
 *  name and a COLUMN name are never case-significant, whatever the mode — see
 *  {@link MYSQL_CASE_INSENSITIVE_PARTS}; only the relation / schema / database
 *  halves of their identities follow the setting. `1` / `2`, and a connection
 *  nothing has probed yet, are case-insensitive throughout.
 *
 *  The same rule, from the same probe, already decides the cursor gate
 *  (`go-to/oeTreeQualification.ts`'s `mysqlComponentCaseSensitive`) and the
 *  identifier index's own object keys (`mysqlCaseProbe.ts`'s
 *  `mysqlObjectKeyCase`); this is the comparator's copy of it, and the three
 *  must not drift.
 *  https://dev.mysql.com/doc/refman/8.0/en/identifier-case-sensitivity.html */
function mysqlCaseSensitiveComponent(
  ctx: RelationIdentityContext,
  part: IdentityComponent,
): boolean {
  return (
    ctx.engine === 'mysql' &&
    ctx.mysqlLowerCaseTableNames === 0 &&
    !MYSQL_CASE_INSENSITIVE_PARTS.has(part)
  );
}

/** True iff identifier equality for this engine/settings ignores case. Applied
 *  to the already-engine-folded component forms, so it only ever LOOSENS the
 *  comparison for the case-insensitive engines. */
function caseInsensitive(ctx: RelationIdentityContext, part: IdentityComponent): boolean {
  switch (ctx.engine) {
    case 'postgres':
    case 'oracle':
      return false; // folded forms are already canonical; compare exactly
    case 'sqlite':
      return true;
    case 'mssql':
      // Default (a `_CI_` database): insensitive. A `_CS_` database never
      // reaches this comparison — {@link catalogComponentEq} answers object
      // and schema components from the SPELLED forms, and
      // {@link databaseComponentEq} keeps database names insensitive.
      return !ctx.mssqlCatalogCaseSensitive;
    case 'mysql':
      // A case-significant component never reaches here either — the SPELLED
      // compare in {@link catalogComponentEq} / {@link databaseComponentEq}
      // answers it. What is left is insensitive, ROUTINE AND COLUMN LEAVES
      // INCLUDED: neither is a filesystem entry, so `lower_case_table_names`
      // never reaches them, and only an insensitive compare lets a body's
      // backticked `` `GetBalance` `` / `` `ID` `` — which the parser keeps
      // verbatim — still reach the catalog's `getbalance` / `id`.
      return !mysqlCaseSensitiveComponent(ctx, part);
    default:
      return false;
  }
}

/** Compare two already-folded identifier components with engine-aware casing. */
function componentEq(
  a: string,
  b: string,
  ctx: RelationIdentityContext,
  part: IdentityComponent,
): boolean {
  return caseInsensitive(ctx, part) ? a.toLowerCase() === b.toLowerCase() : a === b;
}

/** Compare one OBJECT or SCHEMA component — the two SQL Server compares in the
 *  holding database's collation.
 *
 *  On a case-sensitive SQL Server database the comparison is on the SPELLED
 *  component, because the FOLDED forms cannot answer the question there:
 *  parse-time folding (`sql-resolver/scope/identifiers.ts`'s `defaultFolding`)
 *  lowercases an unquoted `dbo.V` to `v` but keeps a bracketed `dbo.[V]` as
 *  `V`. Comparing those folded forms would (a) miss the real difference — the
 *  two views of a `_CS_` database both fold to `v` — and (b) invent one
 *  between `[Orders]` and `Orders`, which SQL Server treats as the same object
 *  whatever the collation, silently dropping every bracketed reference (SSMS
 *  writes brackets by default). The spelled component is delimiter-free and,
 *  in a `_CS_` database, necessarily the catalog case: a body spelling it any
 *  other way would not compile.
 *
 *  MYSQL on `@@lower_case_table_names = 0` is the SAME situation for the SAME
 *  reason. `defaultFolding('mysql')` is `'case-insensitive'`, so the parser
 *  lowercases an unquoted `Users` to `users` and keeps a backticked
 *  `` `Users` `` as `Users` — while `oeIdentifier.ts`'s
 *  `buildOeRelationIdentifier` folds the Object-Explorer's catalog `Users` to
 *  `users`. Under mode 0 the folded compare was exact, so it (a) MISSED every
 *  backticked reference — a real dependent view simply vanished from Find
 *  References — and (b) INVENTED a match between `Users` and `users`, which on
 *  a mode-0 server are two different files and therefore two different tables.
 *  Backticks no more change a MySQL identity than brackets change a T-SQL one,
 *  so the spelled component answers both, and it is necessarily the catalog
 *  case for the same reason: a body spelling a mode-0 table any other way would
 *  not resolve.
 *  https://dev.mysql.com/doc/refman/8.0/en/identifier-case-sensitivity.html
 *
 *  A MySQL ROUTINE leaf and a MySQL COLUMN leaf are deliberately NOT part of
 *  that: neither is a filesystem entry, so `lower_case_table_names` never
 *  reaches them and `'routine-leaf'` / `'column-leaf'` keep the
 *  case-insensitive compare (see {@link MYSQL_CASE_INSENSITIVE_PARTS}). Modes
 *  `1` / `2`, an unprobed connection, every other engine, and a
 *  case-INSENSITIVE SQL Server database all keep the folded comparison
 *  unchanged.
 *
 *  SQL SERVER is explicitly not swept into the column rule: it compares column
 *  names in the collation of the database that holds them, so a `_CS_` database
 *  really does tell `Email` from `email`. The mssql branch below is checked
 *  FIRST and keeps its spelled compare for every component, columns included.
 *
 *  `part` defaults to `'leaf'` because the two components that need naming are
 *  the routine leaf and the column leaf: a relation leaf and a schema get the
 *  SAME answer on every engine, so `relationIdentity.ts` can compare either
 *  without stating which. */
export function catalogComponentEq(
  a: { readonly spelled: string; readonly folded: string },
  b: { readonly spelled: string; readonly folded: string },
  ctx: RelationIdentityContext,
  part: IdentityComponent = 'leaf',
): boolean {
  if (ctx.engine === 'mssql' && ctx.mssqlCatalogCaseSensitive === true) {
    return a.spelled === b.spelled;
  }
  if (mysqlCaseSensitiveComponent(ctx, part)) {
    return a.spelled === b.spelled;
  }
  return componentEq(a.folded, b.folded, ctx, part);
}

/** Compare the DATABASE component. On SQL Server this one is NOT the holding
 *  database's collation — database names are compared in the SERVER
 *  (instance) collation, which `mssqlCatalogCaseSensitive` does not describe.
 *  {@link RelationIdentityContext.mssqlDatabaseNamesCaseSensitive} carries
 *  that one, and on a `_CS_` instance the comparison runs on the SPELLED
 *  halves for the same reason {@link catalogComponentEq} does: mssql folding
 *  lowercases every unquoted identifier, so `Sales` and `sales` — two
 *  databases there — both reach this module folded to `sales`.
 *
 *  MYSQL has no second collation to read: a schema IS a database, so the
 *  DATABASE component takes the schema's rule — case-significant exactly on
 *  `@@lower_case_table_names = 0`, where a database is a directory on disk.
 *  That is the same answer the tree gate gives (`databaseComponentEquals` in
 *  `go-to/oeTreeQualification.ts` routes a MySQL database through
 *  `mysqlComponentCaseSensitive(_, 'schema')`), and it runs on the SPELLED
 *  halves for the reason {@link catalogComponentEq} spells out.
 *
 *  A component whose SPELLED half is empty keeps the case-insensitive compare
 *  on both case-sensitive engines: the question cannot be answered without the
 *  spelling, and over-inclusion is the safe direction. Every production caller
 *  today carries both halves ({@link databaseComponent} falls back to the fold,
 *  and `relationIdentity.ts`'s `effectiveDatabaseFor` sets both), so this is a
 *  guard against a half-built component rather than a path with a named caller
 *  — {@link spelledOrInsensitive} is where it lives, and
 *  `relationIdentity.test.ts` pins it. Every other engine keeps
 *  {@link componentEq} on the folded forms. */
export function databaseComponentEq(
  a: DatabaseComponent,
  b: DatabaseComponent,
  ctx: RelationIdentityContext,
): boolean {
  if (ctx.engine === 'mssql') {
    return ctx.mssqlDatabaseNamesCaseSensitive === true
      ? spelledOrInsensitive(a, b)
      : a.folded.toLowerCase() === b.folded.toLowerCase();
  }
  if (mysqlCaseSensitiveComponent(ctx, 'database')) return spelledOrInsensitive(a, b);
  return componentEq(a.folded, b.folded, ctx, 'database');
}

/** The SPELLED database compare, with the "spelling unknown → stay
 *  case-insensitive" fallback both case-sensitive database rules need. A
 *  component built from a `QualifiedName` that carries a folded database but no
 *  spelled one is the shape that reaches it. */
function spelledOrInsensitive(a: DatabaseComponent, b: DatabaseComponent): boolean {
  if (a.spelled && b.spelled) return a.spelled === b.spelled;
  return a.folded.toLowerCase() === b.folded.toLowerCase();
}

export function foldedLeaf(name: QualifiedName): string {
  return name.foldedName ?? name.name ?? '';
}

export function leafComponent(name: QualifiedName): DatabaseComponent {
  return { spelled: name.name ?? foldedLeaf(name), folded: foldedLeaf(name) };
}

export function schemaComponent(name: QualifiedName): DatabaseComponent {
  const folded = nonEmpty(name.foldedSchema);
  return { spelled: name.schema ?? folded, folded };
}

export function databaseComponent(name: QualifiedName): DatabaseComponent {
  const folded = nonEmpty(name.foldedDatabase);
  return { spelled: name.database ?? folded, folded };
}

/** Normalize the caller-supplied effective database. `undefined` (no database
 *  context at all) becomes the empty component, whose empty `folded` is how
 *  every comparator here spells "skip the database check". */
export function toDatabaseComponent(value: EffectiveDatabase | undefined): DatabaseComponent {
  return value ?? { spelled: '', folded: '' };
}

export function nonEmpty(value: string | undefined): string {
  return value ?? '';
}

/** The SQL Server catalog case rule for the database an index handle was built
 *  for — `undefined` for every other engine (the field is MSSQL-only).
 *
 *  The INDEX is the source, never a fresh lookup by `(connectionId,
 *  database)`: the lifecycle resolves an empty `database` to the connection's
 *  configured one and files that database's probed collation under the
 *  RESOLVED identity, so recomputing it for the `''` the Object Explorer and
 *  the query tab send asks about a database nothing probed and answers
 *  "unknown" — the bug that once folded a `_CS_` database's `dbo.V` and
 *  `dbo.v` into one References group. The handle reports the rule it actually
 *  froze, so matching and grouping cannot drift apart.
 *
 *  `'view'` stands in for "any kind": SQL Server compares schema, table, view,
 *  routine and column names in the ONE collation of the database that holds
 *  them, so every kind's answer is that database's collation. (MySQL is the
 *  engine whose rule differs per kind — it carries its own setting and never
 *  reaches here.) */
export function mssqlCatalogCaseSensitiveFor(
  index: Pick<IdentifierIndex, 'objectKeyCaseFor'>,
  engine: SqlEngine | undefined,
): boolean | undefined {
  if (engine !== 'mssql') return undefined;
  return index.objectKeyCaseFor('view').name;
}

/** Which part of a qualified identity a comparison is about.
 *
 *  `'leaf'` is a RELATION leaf (a table / view name). `'routine-leaf'` is a
 *  stored routine's own name and `'column-leaf'` a column name; both are
 *  separate parts for ONE engine's sake: MySQL's `@@lower_case_table_names`
 *  governs directories (schemas) and files (tables / views), never the routine
 *  or column names inside them, so those two leaves stay case-insensitive there
 *  in every mode. All three extract the same component ({@link leafComponent}),
 *  and every other engine gives them the same answer and never looks at the
 *  difference — PostgreSQL / Oracle compare the canonical folded forms, SQLite
 *  is blanket-insensitive, and SQL Server reads the holding database's
 *  collation for relation, routine and column names alike. */
export type IdentityComponent = 'leaf' | 'routine-leaf' | 'column-leaf' | 'schema' | 'database';

/** Pull one component out of a qualified name in BOTH spellings.
 *
 *  Exported — with {@link componentsEqual} — so `routineIdentity.ts` decides a
 *  routine's schema/database identity with THIS module's folds instead of
 *  inventing a second set. An absent component comes back empty on both
 *  halves, which is how every caller here spells "wildcard". */
export function qualifiedComponent(
  name: QualifiedName,
  part: IdentityComponent,
): DatabaseComponent {
  if (part === 'leaf' || part === 'routine-leaf' || part === 'column-leaf') {
    return leafComponent(name);
  }
  if (part === 'schema') return schemaComponent(name);
  return databaseComponent(name);
}

/** Engine-aware equality for ONE component. `'database'` follows SQL Server's
 *  SERVER (instance) collation; the leaves and `'schema'` follow the holding
 *  DATABASE's — the split {@link databaseComponentEq} and
 *  {@link catalogComponentEq} document. The part is passed on so MySQL can give
 *  a routine leaf and a column leaf their own (always case-insensitive)
 *  answer. */
export function componentsEqual(
  a: DatabaseComponent,
  b: DatabaseComponent,
  part: IdentityComponent,
  ctx: RelationIdentityContext,
): boolean {
  return part === 'database' ? databaseComponentEq(a, b, ctx) : catalogComponentEq(a, b, ctx, part);
}
